/**
 * Test ID Utilities
 *
 * Proporciona funciones helper para generar data-testid consistentes
 * para automatización con Cypress y otros frameworks de testing.
 *
 * @module testIds
 */

/**
 * Convención de nomenclatura para data-testid:
 *
 * Formato: {feature}-{component}-{element}-{action?}
 *
 * Ejemplos:
 * - auth-login-email-input
 * - auth-login-password-input
 * - auth-login-submit-button
 * - invoice-list-create-button
 * - invoice-detail-status-badge
 * - customer-form-name-input
 * - dashboard-stats-revenue-card
 *
 * Reglas:
 * 1. Usar kebab-case (minúsculas con guiones)
 * 2. Ser específico pero conciso
 * 3. Incluir el tipo de elemento al final (input, button, modal, etc.)
 * 4. Usar acción opcional para botones (submit, cancel, delete, edit, etc.)
 */

/**
 * Tipos de elementos comunes para test IDs
 */
export const TestElementType = {
  // Inputs y formularios
  INPUT: 'input',
  TEXTAREA: 'textarea',
  SELECT: 'select',
  CHECKBOX: 'checkbox',
  RADIO: 'radio',

  // Botones y acciones
  BUTTON: 'button',
  LINK: 'link',
  ICON_BUTTON: 'icon-button',

  // Contenedores y layout
  FORM: 'form',
  MODAL: 'modal',
  CARD: 'card',
  CONTAINER: 'container',
  SECTION: 'section',

  // Visualización
  TABLE: 'table',
  ROW: 'row',
  CELL: 'cell',
  LIST: 'list',
  ITEM: 'item',
  BADGE: 'badge',
  ALERT: 'alert',

  // Navegación
  TAB: 'tab',
  MENU: 'menu',
  DROPDOWN: 'dropdown',

  // Otros
  LABEL: 'label',
  TEXT: 'text',
  IMAGE: 'image',
  SPINNER: 'spinner',
} as const;

/**
 * Acciones comunes para botones
 */
export const TestAction = {
  SUBMIT: 'submit',
  CANCEL: 'cancel',
  DELETE: 'delete',
  EDIT: 'edit',
  CREATE: 'create',
  SAVE: 'save',
  CLOSE: 'close',
  CONFIRM: 'confirm',
  SEARCH: 'search',
  FILTER: 'filter',
  EXPORT: 'export',
  IMPORT: 'import',
  DOWNLOAD: 'download',
  UPLOAD: 'upload',
  VIEW: 'view',
  NEXT: 'next',
  PREVIOUS: 'previous',
  BACK: 'back',
  LOGOUT: 'logout',
} as const;

/**
 * Genera un data-testid consistente
 *
 * @param parts - Partes del test ID (feature, component, element, etc.)
 * @returns string - Test ID en formato kebab-case
 *
 * @example
 * ```ts
 * testId('auth', 'login', 'email', 'input')
 * // => 'auth-login-email-input'
 *
 * testId('invoice', 'list', 'create', 'button')
 * // => 'invoice-list-create-button'
 * ```
 */
export function testId(...parts: (string | number | undefined | null)[]): string {
  return parts
    .filter(Boolean)
    .map(part => String(part).toLowerCase().replace(/[^a-z0-9]+/g, '-'))
    .join('-')
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
}

/**
 * Genera test IDs para campos de formulario
 *
 * @param formName - Nombre del formulario
 * @param fieldName - Nombre del campo
 * @returns Object con test IDs para input, label, error, etc.
 *
 * @example
 * ```tsx
 * const ids = formFieldIds('login', 'email');
 * <input data-testid={ids.input} />
 * <label data-testid={ids.label}>Email</label>
 * <span data-testid={ids.error}>Error message</span>
 * ```
 */
export function formFieldIds(formName: string, fieldName: string) {
  const base = testId(formName, fieldName);
  return {
    container: `${base}-container`,
    label: `${base}-label`,
    input: `${base}-input`,
    error: `${base}-error`,
    helper: `${base}-helper`,
  };
}

/**
 * Genera test IDs para modales
 *
 * @param modalName - Nombre del modal
 * @returns Object con test IDs para elementos del modal
 *
 * @example
 * ```tsx
 * const ids = modalIds('delete-customer');
 * <div data-testid={ids.container}>
 *   <h2 data-testid={ids.title}>Confirm Delete</h2>
 *   <button data-testid={ids.confirm}>Delete</button>
 *   <button data-testid={ids.cancel}>Cancel</button>
 * </div>
 * ```
 */
export function modalIds(modalName: string) {
  const base = testId(modalName, 'modal');
  return {
    container: base,
    overlay: `${base}-overlay`,
    title: `${base}-title`,
    content: `${base}-content`,
    confirm: `${base}-confirm-button`,
    cancel: `${base}-cancel-button`,
    close: `${base}-close-button`,
  };
}

/**
 * Genera test IDs para tablas
 *
 * @param tableName - Nombre de la tabla
 * @returns Object con test IDs para elementos de tabla
 *
 * @example
 * ```tsx
 * const ids = tableIds('invoices');
 * <table data-testid={ids.container}>
 *   <thead data-testid={ids.header}>...</thead>
 *   <tbody data-testid={ids.body}>
 *     <tr data-testid={ids.row(0)}>...</tr>
 *   </tbody>
 * </table>
 * ```
 */
export function tableIds(tableName: string) {
  const base = testId(tableName, 'table');
  return {
    container: base,
    header: `${base}-header`,
    body: `${base}-body`,
    row: (index: number) => `${base}-row-${index}`,
    cell: (rowIndex: number, colIndex: number) => `${base}-cell-${rowIndex}-${colIndex}`,
    emptyState: `${base}-empty-state`,
    loading: `${base}-loading`,
  };
}

/**
 * Genera test IDs para listas
 *
 * @param listName - Nombre de la lista
 * @returns Object con test IDs para elementos de lista
 */
export function listIds(listName: string) {
  const base = testId(listName, 'list');
  return {
    container: base,
    item: (index: number) => `${base}-item-${index}`,
    emptyState: `${base}-empty-state`,
    loading: `${base}-loading`,
  };
}

/**
 * Genera test IDs para cards/tarjetas
 *
 * @param cardName - Nombre de la tarjeta
 * @returns Object con test IDs para elementos de tarjeta
 */
export function cardIds(cardName: string) {
  const base = testId(cardName, 'card');
  return {
    container: base,
    title: `${base}-title`,
    content: `${base}-content`,
    footer: `${base}-footer`,
    action: (actionName: string) => `${base}-${actionName}-button`,
  };
}

/**
 * Hook para generar test IDs con prefijo de feature
 *
 * @param feature - Nombre del feature/módulo
 * @returns Función para generar test IDs con el prefijo del feature
 *
 * @example
 * ```tsx
 * function InvoiceList() {
 *   const tid = useTestIds('invoice');
 *
 *   return (
 *     <div data-testid={tid('list', 'container')}>
 *       <button data-testid={tid('list', 'create', 'button')}>
 *         Create Invoice
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useTestIds(feature: string) {
  return (...parts: (string | number | undefined | null)[]) =>
    testId(feature, ...parts);
}

/**
 * Constantes de test IDs para features principales
 * Esto facilita la reutilización y mantiene consistencia
 */
export const TEST_IDS = {
  // Auth
  AUTH: {
    LOGIN: {
      FORM: testId('auth', 'login', 'form'),
      EMAIL_INPUT: testId('auth', 'login', 'email', TestElementType.INPUT),
      PASSWORD_INPUT: testId('auth', 'login', 'password', TestElementType.INPUT),
      REMEMBER_CHECKBOX: testId('auth', 'login', 'remember', TestElementType.CHECKBOX),
      SUBMIT_BUTTON: testId('auth', 'login', TestAction.SUBMIT, TestElementType.BUTTON),
      FORGOT_PASSWORD_LINK: testId('auth', 'login', 'forgot-password', TestElementType.LINK),
      ERROR_ALERT: testId('auth', 'login', 'error', TestElementType.ALERT),
    },
    FORGOT_PASSWORD: {
      FORM: testId('auth', 'forgot-password', 'form'),
      EMAIL_INPUT: testId('auth', 'forgot-password', 'email', TestElementType.INPUT),
      SUBMIT_BUTTON: testId('auth', 'forgot-password', TestAction.SUBMIT, TestElementType.BUTTON),
      CODE_INPUT: testId('auth', 'forgot-password', 'code', TestElementType.INPUT),
      PASSWORD_INPUT: testId('auth', 'forgot-password', 'password', TestElementType.INPUT),
      CONFIRM_PASSWORD_INPUT: testId('auth', 'forgot-password', 'confirm-password', TestElementType.INPUT),
      RESET_SUBMIT_BUTTON: testId('auth', 'forgot-password', 'reset-submit', TestElementType.BUTTON),
      BACK_BUTTON: testId('auth', 'forgot-password', TestAction.BACK, TestElementType.BUTTON),
      SUCCESS_ALERT: testId('auth', 'forgot-password', 'success', TestElementType.ALERT),
    },
    RESET_PASSWORD: {
      FORM: testId('auth', 'reset-password', 'form'),
      PASSWORD_INPUT: testId('auth', 'reset-password', 'password', TestElementType.INPUT),
      CONFIRM_PASSWORD_INPUT: testId('auth', 'reset-password', 'confirm-password', TestElementType.INPUT),
      SUBMIT_BUTTON: testId('auth', 'reset-password', TestAction.SUBMIT, TestElementType.BUTTON),
    },
  },

  // Dashboard
  DASHBOARD: {
    CONTAINER: testId('dashboard', 'container'),
    START_DATE_INPUT: testId('dashboard', 'start-date', TestElementType.INPUT),
    END_DATE_INPUT: testId('dashboard', 'end-date', TestElementType.INPUT),
    UPDATE_BUTTON: testId('dashboard', 'update', TestElementType.BUTTON),
    LOADING: testId('dashboard', TestElementType.SPINNER),
    ERROR_ALERT: testId('dashboard', 'error', TestElementType.ALERT),
    STATS: {
      TOTAL_INVOICES: testId('dashboard', 'stats', 'total-invoices', TestElementType.CARD),
      REVENUE: testId('dashboard', 'stats', 'revenue', TestElementType.CARD),
      PENDING_INVOICES: testId('dashboard', 'stats', 'pending-invoices', TestElementType.CARD),
      APPROVED_INVOICES: testId('dashboard', 'stats', 'approved-invoices', TestElementType.CARD),
      REJECTED_INVOICES: testId('dashboard', 'stats', 'rejected-invoices', TestElementType.CARD),
      ACTIVE_CUSTOMERS: testId('dashboard', 'stats', 'active-customers', TestElementType.CARD),
      DOCUMENT_TYPES: testId('dashboard', 'stats', 'document-types', TestElementType.CARD),
      AVERAGE_INVOICE: testId('dashboard', 'stats', 'average-invoice', TestElementType.CARD),
    },
    CHARTS: {
      MONTHLY: testId('dashboard', 'chart', 'monthly', TestElementType.CARD),
      STATUS_DISTRIBUTION: testId('dashboard', 'chart', 'status-distribution', TestElementType.CARD),
    },
    TABLES: {
      TOP_CUSTOMERS: testId('dashboard', 'table', 'top-customers'),
      TOP_DOCUMENTS: testId('dashboard', 'table', 'top-documents'),
    },
  },

  // Navigation
  NAV: {
    SIDEBAR: testId('nav', 'sidebar'),
    SIDEBAR_DESKTOP: testId('nav', 'sidebar', 'desktop'),
    SIDEBAR_MOBILE: testId('nav', 'sidebar', 'mobile'),
    SIDEBAR_TOGGLE: testId('nav', 'sidebar', 'toggle', TestElementType.BUTTON),
    MOBILE_MENU_BUTTON: testId('nav', 'mobile-menu', TestElementType.BUTTON),
    LOGOUT_BUTTON: testId('nav', TestAction.LOGOUT, TestElementType.BUTTON),
    TENANT_SWITCHER: testId('nav', 'tenant-switcher'),
    USER_MENU: testId('nav', 'user', TestElementType.MENU),
    DASHBOARD_LINK: testId('nav', 'dashboard', TestElementType.LINK),
    INVOICES_LINK: testId('nav', 'invoices', TestElementType.LINK),
    CUSTOMERS_LINK: testId('nav', 'customers', TestElementType.LINK),
    PRODUCTS_LINK: testId('nav', 'products', TestElementType.LINK),
    NEW_INVOICE_LINK: testId('nav', 'new-invoice', TestElementType.LINK),
  },

  // Common
  COMMON: {
    LOADING: testId('common', TestElementType.SPINNER),
    MODAL_OVERLAY: testId('common', 'modal', 'overlay'),
  },
} as const;
